import React, { useMemo, useRef } from 'react';
import * as THREE from 'three';
import { Text } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';

interface BeakerProps {
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: number;
  waterLevel?: number; // 0 to 1
  waterTemp?: number;
  opacity?: number;
  liquidColor?: string;
}

export function Beaker({ 
  position = [0, 0, 0], 
  rotation = [0, 0, 0], 
  scale = 1, 
  waterLevel = 0.5, 
  waterTemp = 25, 
  opacity = 0.15,
  liquidColor = '#ffffff' // Pure transparent water color
}: BeakerProps) {
  // === ACCURATE 250mL CORNING PYREX HEAVY-DUTY BEAKER DIMENSIONS ===
  const radius = 0.17;
  const beakerHeight = 0.40;
  const maxBeakerVolume = 300; // Visual height represents ~300mL slightly below brim

  // Water level clamped
  const fillLevel = Math.max(0, Math.min(1, waterLevel));
  const totalLiquidHeight = fillLevel * beakerHeight * 0.9;
  const liquidBottomY = -beakerHeight / 2 + 0.006;
  const liquidY = liquidBottomY + totalLiquidHeight / 2;

  const getHeightForVolume = (vol: number) => {
    return (vol / maxBeakerVolume) * beakerHeight;
  };

  // --- REALISTIC BOILING ANIMATION ---
  const bubblesRef = useRef<THREE.InstancedMesh>(null);
  const bubbleCount = 80;
  
  const dummy = useMemo(() => new THREE.Object3D(), []);
  
  // Precompute random initial states for each bubble
  const initialBubbleData = useMemo(() => {
    return Array.from({ length: bubbleCount }, () => ({
      x: (Math.random() - 0.5) * (radius * 1.4),
      z: (Math.random() - 0.5) * (radius * 1.4),
      y: Math.random() * totalLiquidHeight,
      baseSpeed: 0.05 + Math.random() * 0.15,
      phaseOffset: Math.random() * Math.PI * 2,
      scaleMult: 0.5 + Math.random() * 1.0
    }));
  }, [bubbleCount, radius, totalLiquidHeight]);

  // Keep track of dynamic boiling properties per bubble across frames
  const bubbleState = useRef<{y: number; xOffset: number; zOffset: number}[]>(
    initialBubbleData.map((data) => ({ y: data.y, xOffset: 0, zOffset: 0 }))
  );

  useFrame((state, delta) => {
    if (!bubblesRef.current) return;

    // Heat dictates boiling probability and speed (starts bubbling mildly around 85C, full boil at 100C)
    const boilingThreshold = 85;
    
    if (waterTemp >= boilingThreshold && totalLiquidHeight > 0.01) {
      // 0.0 at 85C, up to 1.0 at 100C
      let boilingIntensity = (waterTemp - boilingThreshold) / (100 - boilingThreshold);
      boilingIntensity = Math.min(1, Math.max(0, boilingIntensity));
      
      const activeBubbles = Math.floor(bubbleCount * boilingIntensity);

      for (let i = 0; i < bubbleCount; i++) {
        if (i < activeBubbles) {
          const data = initialBubbleData[i];
          const bState = bubbleState.current[i];
          
          // Bubbles rise faster when hotter
          bState.y += data.baseSpeed * (0.2 + boilingIntensity * 0.8) * delta * 5;

          // Add chaotic wiggle (convection currents)
          const time = state.clock.elapsedTime;
          bState.xOffset = Math.sin(time * 8 + data.phaseOffset) * 0.02 * boilingIntensity;
          bState.zOffset = Math.cos(time * 7 + data.phaseOffset) * 0.02 * boilingIntensity;

          // If bubble reaches surface, reset to bottom
          if (bState.y > totalLiquidHeight * 0.95) {
            bState.y = 0;
            // randomize position upon respawn slightly
            data.x = (Math.random() - 0.5) * (radius * 1.4);
            data.z = (Math.random() - 0.5) * (radius * 1.4);
          }

          dummy.position.set(
            data.x + bState.xOffset,
            liquidBottomY + bState.y,
            data.z + bState.zOffset
          );
          
          // Bubbles expand slightly as they rise due to dropped pressure
          const depthRatio = bState.y / totalLiquidHeight;
          const currentScale = 0.002 * data.scaleMult * (1 + depthRatio * 0.5);
          dummy.scale.set(currentScale, currentScale, currentScale);
          
          dummy.updateMatrix();
          bubblesRef.current.setMatrixAt(i, dummy.matrix);
        } else {
          // Hide inactive bubbles
          dummy.position.set(0, -999, 0);
          dummy.updateMatrix();
          bubblesRef.current.setMatrixAt(i, dummy.matrix);
        }
      }
      bubblesRef.current.instanceMatrix.needsUpdate = true;
    } else {
      // Hide all bubbles if not boiling
      for (let i = 0; i < bubbleCount; i++) {
        dummy.position.set(0, -999, 0);
        dummy.scale.set(0,0,0);
        dummy.updateMatrix();
        bubblesRef.current.setMatrixAt(i, dummy.matrix);
      }
      bubblesRef.current.instanceMatrix.needsUpdate = true;
    }
  });

  // Graduation marks - major marks every 50mL
  const gradMarks = useMemo(() => {
    return [50, 100, 150, 200, 250].map((vol) => {
      const markHeight = getHeightForVolume(vol);
      const y = -beakerHeight / 2 + markHeight;
      return { vol, y, r: radius };
    });
  }, [radius, beakerHeight]);

  // Minor graduation marks - every 10mL
  const minorGradMarks = useMemo(() => {
    const marks: number[] = [];
    for (let vol = 10; vol <= 290; vol += 10) {
      if (vol % 50 !== 0) marks.push(vol);
    }
    return marks.map((vol) => {
      const markHeight = getHeightForVolume(vol);
      const y = -beakerHeight / 2 + markHeight;
      return { vol, y, r: radius };
    });
  }, [radius, beakerHeight]);

  // Micro graduation marks - every 5mL
  const microGradMarks = useMemo(() => {
    const marks: number[] = [];
    for (let vol = 5; vol <= 295; vol += 5) {
      if (vol % 10 !== 0) marks.push(vol);
    }
    return marks.map((vol) => {
      const markHeight = getHeightForVolume(vol);
      const y = -beakerHeight / 2 + markHeight;
      return { vol, y, r: radius };
    });
  }, [radius, beakerHeight]);

  // Opacity-based glass material
  const glassMaterial = (
    <meshPhysicalMaterial
      transparent={true}
      opacity={opacity}
      roughness={0.02}
      metalness={0.05}
      reflectivity={0.9}
      ior={1.47}
      clearcoat={1}
      clearcoatRoughness={0.02}
      color="#dcf0ff" // faint pyrex glass tint
      side={THREE.DoubleSide}
      depthWrite={false}
    />
  );

  return (
    <group position={position} rotation={rotation} scale={scale}>
      
      {/* Offset so base of beaker matches Y=0 internally; Beaker sits *on* position */}
      <group position={[0, beakerHeight / 2, 0]}>
        
        {/* === LIQUID === */}
        {totalLiquidHeight > 0.01 && (
          <mesh position={[0, liquidY, 0]} renderOrder={0}>
            <cylinderGeometry args={[radius - 0.008, radius - 0.008, totalLiquidHeight, 64]} />
            <meshPhysicalMaterial
              color={liquidColor}
              transparent={true}
              opacity={0.9}
              roughness={0.0}
              metalness={0}
              transmission={0.95} // High transmission for realistic liquid transparency
              ior={1.33} // Water's physical Index of Refraction
              thickness={radius * 2} // Volume thickness to make refraction accurate
              attenuationColor="#e0f7fa" // Very faint blue absorption tint
              attenuationDistance={1.0}
              depthWrite={false}
              side={THREE.DoubleSide}
            />
          </mesh>
        )}

        {/* Meniscus */}
        {totalLiquidHeight > 0.01 && (
          <mesh position={[0, liquidY + totalLiquidHeight / 2, 0]} renderOrder={0}>
            <cylinderGeometry args={[radius - 0.006, radius - 0.008, 0.002, 48]} />
            <meshPhysicalMaterial
              color={liquidColor}
              transparent={true}
              opacity={0.8}
              roughness={0.0}
              metalness={0.1}
              transmission={0.8}
              ior={1.33}
              depthWrite={false}
              side={THREE.DoubleSide}
            />
          </mesh>
        )}

        {/* BOILING BUBBLES */}
        {totalLiquidHeight > 0.01 && (
          <instancedMesh
            ref={bubblesRef}
            args={[null, null, bubbleCount]}
            renderOrder={0}
          >
            <sphereGeometry args={[1, 8, 8]} />
            <meshPhysicalMaterial
              color="#ffffff"
              transparent={true}
              opacity={0.8}
              roughness={0.0}
              transmission={0.9} // Glassy bubbles inside water
              ior={1.0} // Air bubble refraction
              attenuationColor="#ffffff"
            />
          </instancedMesh>
        )}

        {/* === BEAKER GLASS === */}
        <mesh castShadow renderOrder={1}>
          <cylinderGeometry args={[radius, radius, beakerHeight, 64, 1, true]} />
          {glassMaterial}
        </mesh>

        {/* Solid Glass Bottom */}
        <mesh position={[0, -beakerHeight / 2 + 0.002, 0]} rotation={[0, 0, 0]} renderOrder={1}>
          <cylinderGeometry args={[radius, radius, 0.01, 64]} />
          {glassMaterial}
        </mesh>

        {/* Top heavy-duty rim */}
        <mesh position={[0, beakerHeight / 2, 0]} rotation={[Math.PI / 2, 0, 0]} renderOrder={1}>
          <torusGeometry args={[radius + 0.006, 0.018, 12, 64]} />
          {glassMaterial}
        </mesh>

        {/* PYREX LOGO / Branding Area */}
        <Text
          position={[0, beakerHeight / 2 - 0.05, radius + 0.001]}
          rotation={[0, 0, 0]}
          fontSize={0.035}
          color="#ffffff"
          anchorX="center"
          anchorY="middle"
          fontWeight={900}
          material-opacity={0.9}
        >
          250 ml
        </Text>

        {/* === MAJOR GRADUATION MARKS (50, 100, 150, 200, 250 mL) === */}
        {gradMarks.map(({ vol, y, r }) => (
          <group key={vol} position={[0, y, 0]}>
            <mesh>
              {/* Longer mark line on the front face */}
              <cylinderGeometry args={[r + 0.001, r + 0.001, 0.002, 32, 1, true, -Math.PI / 4, Math.PI / 2]} />
              <meshBasicMaterial color="#ffffff" side={THREE.DoubleSide} transparent opacity={0.95} />
            </mesh>
            <Text
              position={[(r + 0.012) * Math.sin(Math.PI / 4), 0, (r + 0.012) * Math.cos(Math.PI / 4)]}
              rotation={[0, 0, 0]}
              fontSize={0.020}
              color="#ffffff"
              anchorX="left"
              anchorY="middle"
              fontWeight={600}
            >
              {vol}
            </Text>
          </group>
        ))}

        {/* === MINOR GRADUATION MARKS (10, 20, 30, 40, 60, etc - every 10mL) - MEDIUM === */}
        {minorGradMarks.map(({ vol, y, r }) => (
          <group key={`minor-${vol}`} position={[0, y, 0]}>
            <mesh>
              <cylinderGeometry args={[r + 0.001, r + 0.001, 0.0015, 32, 1, true, -Math.PI / 6, Math.PI / 3]} />
              <meshBasicMaterial color="#ffffff" side={THREE.DoubleSide} transparent opacity={0.75} />
            </mesh>
          </group>
        ))}

        {/* === MICRO GRADUATION MARKS (5, 15, 25, etc - every 5mL) - SMALL === */}
        {microGradMarks.map(({ vol, y, r }) => (
          <group key={`micro-${vol}`} position={[0, y, 0]}>
            <mesh>
              <cylinderGeometry args={[r + 0.001, r + 0.001, 0.0012, 32, 1, true, -Math.PI / 12, Math.PI / 6]} />
              <meshBasicMaterial color="#ffffff" side={THREE.DoubleSide} transparent opacity={0.5} />
            </mesh>
          </group>
        ))}

      </group>
    </group>
  );
}
